import { Component, OnInit } from '@angular/core';
import { ApplyColumnStateParams, ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { AgGridColumns, AgGridOptionsBuilder } from '../../../Compliance/AgGrid';
import {
    AgGridMultiSelectedCellRenderer,
    AgGridMultiSelectedHeaderRenderer,
    AgGridMultiSelectRendererParams,
    AgGridMultiSelectTracker,
} from '../../../Compliance/AgGrid/MultiSelectTracker';
import { IWeissmanModalComponent } from '../../../Compliance/WeissmanModalService';
import {
    ParcelTypeCellRendererComponent,
    ParcelTypeCellRendererComponentParams,
} from '../../../Entity/Parcel/Parcel-Type-Cell-Renderer/parcelTypeCellRenderer.component';
import { UpgradeNavigationServiceHandler } from '../../Routing/upgrade-navigation-handler.service';
import { ActivityStatusCellRendererComponent } from './Activity-Status-Cell-Renderer/activityStatusCellRenderer.component';
import { ActiveStatuses } from '../../../constants.new';

interface ParcelFilterModalComponentParams {
    parcels: any[];
    selectedParcels: number[];
}

@Component({
    selector: 'parcel-filter-modal',
    templateUrl: './parcelFilterModal.component.html',
    standalone: false,
})
export class ParcelFilterModalComponent
    implements OnInit, IWeissmanModalComponent<ParcelFilterModalComponentParams, number[]>
{
    constructor(
        private readonly _bsModalRef: BsModalRef,
        private readonly _navigationService: UpgradeNavigationServiceHandler,
    ) {}

    params: ParcelFilterModalComponentParams;
    result: number[] = [];

    excludeInactive: boolean = true;
    parcels: any[] = [];
    gridTracker: AgGridMultiSelectTracker;
    gridId: System.Guid = '9bb2d71b-40cd-437b-9941-2aa1238a5ae0';
    gridOptions: GridOptions = new AgGridOptionsBuilder({
        rowClassRules: {
            'ag-row-selected': (params) => {
                const parcel = params.data;
                return parcel && this.gridTracker.isRowSelected(parcel.parcelID);
            },
        },
    })
        .withContext(this)
        .withLoadingOverlay()
        .withMultipleColumnSort()
        .withColumnResize()
        .withTextSelection()
        .withRowId((data) => {
            const parcel = data.data;
            return parcel && parcel.parcelID.toString();
        })
        .withColumnPinning()
        .build();

    siteId: number;

    private _allParcels: any[] = [];
    private _gridApi: GridApi;

    get isValid(): boolean {
        return this.gridTracker?.hasAnythingSelected();
    }

    ngOnInit(): void {
        this.siteId = +this._navigationService.getQuerystringParam('siteId');
        const excludeInactiveStored = sessionStorage[`ws_analysisExcludeInactive${this.siteId}`];
        if (excludeInactiveStored) {
            this.excludeInactive = JSON.parse(excludeInactiveStored);
        } else {
            sessionStorage[`ws_analysisExcludeInactive${this.siteId}`] = JSON.stringify(true);
            this.excludeInactive = true;
        }
        this._allParcels = this.params.parcels;

        this.parcels = this.excludeInactive
            ? this._allParcels.filter((x) => x.activityStatus === 'Active (Pending)' || x.activityStatus === 'Active')
            : this._allParcels;
    }

    onAgGridReady(event: GridReadyEvent): void {
        this._gridApi = event.api;

        this.gridTracker = new AgGridMultiSelectTracker(
            this.gridOptions,
            this._gridApi,
            this._getGridRowIds.bind(this),
        );

        if (this.parcels) {
            this._setColumnDefinitions();
        }

        this._setRowData();

        const selectAllRows = this.params.selectedParcels.length === this.parcels.length;
        this.gridTracker.setSelectedRowsModel({
            selectAllRows: selectAllRows,
            selectedRows: selectAllRows ? [] : [...this.params.selectedParcels],
        });
        this._gridApi.redrawRows();
    }

    cancel(): void {
        this.result = null;
        this._bsModalRef.hide();
    }

    updateParcels(excludeInactive: boolean): void {
        this.excludeInactive = excludeInactive;
        sessionStorage[`ws_analysisExcludeInactive${this.siteId}`] = JSON.stringify(this.excludeInactive);
        this.parcels = this._allParcels.filter(
            (x) => !excludeInactive || x.activityStatus === 'Active (Pending)' || x.activityStatus === 'Active',
        );
        this.gridTracker.clear();
        this._setRowData();
    }

    async updateParcelFilter(): Promise<void> {
        this.result = await this.gridTracker.getSelectedRowIds();
        if (this.result.length === 0) {
            this.result = this._allParcels.map((x) => x.parcelID);
        }
        this._bsModalRef.hide();
    }

    private _setColumnDefinitions(): void {
        const columns: ColDef[] = [
            {
                colId: 'grid-column-select',
                field: 'parcelID',
                width: AgGridColumns.selectionColumnWidth,
                suppressSizeToFit: true,
                resizable: false,
                suppressColumnsToolPanel: true,
                pinned: 'left',
                lockPinned: true,
                editable: false,
                headerComponent: AgGridMultiSelectedHeaderRenderer,
                headerComponentParams: {
                    tracker: this.gridTracker,
                } as AgGridMultiSelectRendererParams,
                cellRenderer: AgGridMultiSelectedCellRenderer,
                cellRendererParams: {
                    tracker: this.gridTracker,
                } as AgGridMultiSelectRendererParams,
            },
            {
                headerName: 'Type',
                field: 'propType',
                width: 50,
                suppressSizeToFit: true,
                suppressAutoSize: true,
                cellRenderer: ParcelTypeCellRendererComponent,
                cellRendererParams: {
                    getParcelType: (params: ParcelTypeCellRendererComponentParams): string => {
                        const parcel = params.data;
                        return parcel.propertyType;
                    },
                },
            },
            {
                headerName: 'Acct Num',
                field: 'accountNumber',
                lockVisible: true,
                valueFormatter: (params) => {
                    const parcel = params.data;
                    return !parcel.isTotalRow ? parcel.acctNum : 'TOTAL';
                },
            },
            {
                headerName: 'Status',
                field: 'activityStatus',
                lockVisible: true,
                cellRenderer: ActivityStatusCellRendererComponent,
            },
        ];

        const defaultSortModel: ApplyColumnStateParams = {
            state: [{ colId: 'acctNum', sort: 'asc' }],
            defaultState: { sort: null },
        };

        this._gridApi.setGridOption('columnDefs', columns);
        this._gridApi.applyColumnState(defaultSortModel);
    }

    private _setRowData(): void {
        if (!this._gridApi) {
            return;
        }

        this.gridTracker && this.gridTracker.clear();

        this._gridApi.setGridOption('rowData', this.parcels);
    }

    private _getGridRowIds(skip, take): Promise<Compliance.QueryResultModel<number>> {
        const model = { rowsToDisplay: [] };
        this._gridApi.forEachNode((x) => model.rowsToDisplay.push(x));
        const rows = model.rowsToDisplay.slice(skip, take + 1);
        const queryResultModel: Compliance.QueryResultModel<number> = {
            lastModifiedTimestamp: new Date(),
            totalRows: rows.length,
            totalValidRows: rows.length,
            data: rows.map((x) => {
                const parcel = x.data;
                return parcel && parcel.parcelID;
            }),
        };

        return Promise.resolve(queryResultModel);
    }
}
